import {
  FormControl,
  FormLabel,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Button,
  Input,
  Spinner,
  Tag,
  TagLabel,
  TagCloseButton,
  Wrap,
  WrapItem,
  HStack,
  Badge,
  useDisclosure,
  InputGroup,
  InputLeftElement,
  useToast,
  Box,
  Tooltip,
  Select,
  SimpleGrid,
} from "@chakra-ui/react"
import { useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router-dom"
import {
  fetchPublicCoreLocationsAsync,
  selectPublicCoreLocations,
} from "../../jobSearch/FetchLocationSlice/FetchLocationSlice"
import { useForm } from "react-hook-form"
import {
  fetchSuggestedSkillsAsync,
  selectSelectedSkills,
} from "../../employers/Employers Slice/employersJobCreateSlice/createJobSlice"
import {
  fetchCoreEducationAsync,
  selectCoreEducations,
} from "../../employers/Employers Slice/employersJobCreateSlice/createJobSlice"
import { SearchIcon } from "@chakra-ui/icons"

const CandidateSearchForm = () => {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    setError,
    clearErrors,
    formState: { errors },
  } = useForm({
    defaultValues: {
      skills: [],
      work_status: "",
      notice_period: "",
      experience_min: "",
      experience_max: "",
      locations: [],
      educations: [],
      candidate_gender: "",
    },
    mode: "onSubmit",
    reValidateMode: "onChange",
  })

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const toast = useToast()

  // Redux data
  const coreSkills = useSelector(selectSelectedSkills)
  const locations = useSelector(selectPublicCoreLocations)
  const coreEducations = useSelector(selectCoreEducations)

  // Loading states from slices
  const createJobStatus = useSelector((state) => state.createJobReducers.status)
  const locationsLoading = useSelector((state) => state.publicCoreLocations.loading)
  const isSuggestLoading = createJobStatus === "loading"

  // Watchers
  const workStatus = watch("work_status")
  const isFresher = workStatus === "fresher"

  // Menu disclosures
  const skillsMenu = useDisclosure()
  const locationsMenu = useDisclosure()

  // Local state for selections
  const [skills, setSkills] = useState([])
  const [skillsId, setSkillsId] = useState([])

  const [selectedLocations, setSelectedLocations] = useState([])
  const [locationIds, setLocationIds] = useState([])

  const [selectedEducations, setSelectedEducations] = useState([]) // array of { uuid, title } where title is "Level / Course / Speciality"
  const [educationIds, setEducationIds] = useState([])

  // Search inputs with debouncing
  const [skillSearch, setSkillSearch] = useState("")
  const [locationSearch, setLocationSearch] = useState("")

  // Hierarchical Education picker states
  const [selectedEduLevel, setSelectedEduLevel] = useState(null)
  const [selectedEduCourse, setSelectedEduCourse] = useState(null)
  const [selectedEduSpeciality, setSelectedEduSpeciality] = useState(null)

  useEffect(() => {
    dispatch(fetchSuggestedSkillsAsync())
    dispatch(fetchPublicCoreLocationsAsync())
    dispatch(fetchCoreEducationAsync())
  }, [dispatch])

  // Fresher clears and disables some fields
  useEffect(() => {
    if (isFresher) {
      setValue("experience_min", "")
      setValue("experience_max", "")
      setValue("notice_period", "")
      clearErrors(["experience_min", "experience_max", "notice_period"])
    }
  }, [isFresher, setValue, clearErrors])

  // RHF register hidden fields
  useEffect(() => {
    register("skills", { required: "Please select at least one skill." })
    register("locations")
    register("educations")
    register("candidate_gender")
  }, [register])

  // Debounce skill search
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (skillSearch || "").trim()
      if (q.length > 0) {
        dispatch(fetchSuggestedSkillsAsync(q))
      } else {
        dispatch(fetchSuggestedSkillsAsync())
      }
    }, 500)
    return () => clearTimeout(handler)
  }, [skillSearch, dispatch])

  // Debounce location search
  useEffect(() => {
    const handler = setTimeout(() => {
      const q = (locationSearch || "").trim()
      if (q.length > 0) {
        dispatch(fetchPublicCoreLocationsAsync({ keyword: q }))
      } else {
        dispatch(fetchPublicCoreLocationsAsync())
      }
    }, 500)
    return () => clearTimeout(handler)
  }, [locationSearch, dispatch])

  // Education lists derived from coreEducations
  const levels = useMemo(() => (Array.isArray(coreEducations) ? coreEducations : []), [coreEducations])
  const courses = useMemo(
    () => (selectedEduLevel?.core_job_education_course || []),
    [selectedEduLevel]
  )
  const specialities = useMemo(
    () => (selectedEduCourse?.core_job_education_course_specialty || []),
    [selectedEduCourse]
  )

  // Education helpers
  const resetCourseAndBelow = () => {
    setSelectedEduCourse(null)
    setSelectedEduSpeciality(null)
  }
  const resetSpeciality = () => setSelectedEduSpeciality(null)
  const buildEduLabel = (level, course, spec) =>
    [level?.title, course?.title, spec?.title].filter(Boolean).join(" / ")

  // Add the selected education chain as one tag (last selected UUID wins)
  const addSelectedEducation = () => {
    if (!selectedEduLevel) return
    const finalObj = selectedEduSpeciality || selectedEduCourse || selectedEduLevel
    const finalUuid = finalObj.uuid
    const label = buildEduLabel(selectedEduLevel, selectedEduCourse, selectedEduSpeciality)

    if (educationIds.includes(finalUuid)) return // prevent duplicates

    const newItem = { uuid: finalUuid, title: label }
    const updated = [...selectedEducations, newItem]
    const updatedIds = [...educationIds, finalUuid]

    setSelectedEducations(updated)
    setEducationIds(updatedIds)
    setValue("educations", updatedIds)

    // reset picker
    setSelectedEduLevel(null)
    resetCourseAndBelow()
  }

  // Select skill
  const handleSelectSkillItem = (item) => {
    if (!item) return
    if (skills.some((s) => s.uuid === item.uuid)) return

    const updateSkill = [...skills, item]
    const updateSkillId = updateSkill.map((s) => s.uuid)

    setSkills(updateSkill)
    setSkillsId(updateSkillId)
    setValue("skills", updateSkillId, { shouldValidate: true })
    clearErrors("skills")

    // Close and clear search for next pick
    skillsMenu.onClose()
    setSkillSearch("")
  }

  // Select location
  const handleSelectLocationItem = (item) => {
    if (!item) return
    if (selectedLocations.some((l) => l.uuid === item.uuid)) return

    const updatedLocations = [...selectedLocations, item]
    const updatedLocationIds = updatedLocations.map((l) => l.uuid)

    setSelectedLocations(updatedLocations)
    setLocationIds(updatedLocationIds)
    setValue("locations", updatedLocationIds)

    locationsMenu.onClose()
    setLocationSearch("")
  }

  const removeSkill = (index) => {
    const updatedSkills = skills.filter((_, i) => i !== index)
    const updatedSkillIds = updatedSkills.map((skill) => skill.uuid)

    setSkills(updatedSkills)
    setSkillsId(updatedSkillIds)
    setValue("skills", updatedSkillIds, { shouldValidate: true })
    if (updatedSkillIds.length === 0) {
      setError("skills", { type: "manual", message: "Please select at least one skill." })
    }
  }

  const removeLocation = (index) => {
    const updatedLocations = selectedLocations.filter((_, i) => i !== index)
    const updatedLocationIds = updatedLocations.map((location) => location.uuid)

    setSelectedLocations(updatedLocations)
    setLocationIds(updatedLocationIds)
    setValue("locations", updatedLocationIds)
  }

  const removeEducation = (index) => {
    const updatedEducations = selectedEducations.filter((_, i) => i !== index)
    const updatedEducationIds = updatedEducations.map((edu) => edu.uuid)

    setSelectedEducations(updatedEducations)
    setEducationIds(updatedEducationIds)
    setValue("educations", updatedEducationIds)
  }

  // Simple exp max >= min validation on submit
  const onSubmit = (data) => {
    if (skillsId.length === 0) {
      setError("skills", { type: "manual", message: "Please select at least one skill." })
      toast({
        title: "Add at least one skill",
        status: "warning",
        duration: 2000,
        isClosable: true,
        position: "top-right",
      })
      return
    }

    if (!isFresher) {
      const min = data.experience_min !== "" ? Number(data.experience_min) : null
      const max = data.experience_max !== "" ? Number(data.experience_max) : null
      if (min !== null && max !== null && max < min) {
        toast({
          title: "Experience range invalid",
          description: "Maximum experience should be greater than or equal to minimum experience.",
          status: "error",
          duration: 2500,
          isClosable: true,
          position: "top-right",
        })
        return
      }
    }

    const searchCriteria = {
      notice_period: isFresher ? "" : data.notice_period,
      work_status: data.work_status,
      experience_min: isFresher ? "" : data.experience_min,
      experience_max: isFresher ? "" : data.experience_max,
      skills: skillsId,
      locations: locationIds,
      educations: educationIds, // last-selected UUIDs for each education chain
      candidate_gender: data.candidate_gender || "",
    }

    navigate("/employer-dashboard/candidate-profile-list", { state: searchCriteria })
  }

  return (
    <div className="">
      <div className="bg-mainBg p-3">
        <div className="px-2 sm:px-4 sm:py-4 min-h-screen">
          <div>
            <h1 className="sm:text-4xl text-2xl font-semibold mb-4 text-left">Search Candidate</h1>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="bg-white p-6 rounded-xl mx-auto" noValidate>
            {/* Work status */}
            <div className="pb-4 border-b mb-6">
              <h4 className="text-lg font-semibold mb-3">Searching For</h4>
              <div className="flex gap-6 flex-wrap">
                {["fresher", "experienced", "any"].map((status) => (
                  <label key={status} className="flex items-center cursor-pointer">
                    <input type="radio" value={status} {...register("work_status")} className="mr-2" />
                    <span>{status.charAt(0).toUpperCase() + status.slice(1)}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Fresher disables notice period and experience fields automatically.
              </p>
            </div>

            {/* Skills */}
            <FormControl mb={4} isInvalid={!!errors.skills}>
              <FormLabel>
                Skills{" "}
                <Badge ml={2} colorScheme="blue" variant="subtle">
                  {skills.length} selected
                </Badge>
              </FormLabel>
              <Menu
                isOpen={skillsMenu.isOpen}
                onOpen={() => {
                  skillsMenu.onOpen()
                  setSkillSearch("")
                }}
                onClose={() => {
                  skillsMenu.onClose()
                  setSkillSearch("")
                }}
                closeOnSelect
                autoSelect={false}
              >
                <MenuButton as={Button} variant="outline" width="100%" justifyContent="space-between" fontWeight="normal">
                  Add skills
                </MenuButton>
                <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Type to search skills..."
                      value={skillSearch}
                      onChange={(e) => setSkillSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault()
                      }}
                      autoFocus
                    />
                  </InputGroup>
                  <div className="max-h-64 overflow-y-auto mt-2">
                    {isSuggestLoading ? (
                      <div className="flex items-center gap-2 p-3">
                        <Spinner size="sm" />
                        <span>Searching...</span>
                      </div>
                    ) : Array.isArray(coreSkills) && coreSkills.length > 0 ? (
                      coreSkills.map((item) => {
                        const alreadySelected = skills.some((s) => s.uuid === item.uuid)
                        return (
                          <MenuItem key={item.uuid} onClick={() => handleSelectSkillItem(item)} isDisabled={alreadySelected}>
                            <div className="w-full flex items-center">
                              <span>{item.title}</span>
                              {alreadySelected && (
                                <span className="ml-auto text-xs text-blue-500 font-medium">Added</span>
                              )}
                            </div>
                          </MenuItem>
                        )
                      })
                    ) : (
                      <div className="p-3 text-gray-500">No results</div>
                    )}
                  </div>
                </MenuList>
              </Menu>

              <input type="hidden" {...register("skills", { required: "Please select at least one skill." })} />
              {errors.skills && <p className="text-red-500 text-sm mt-1">{errors.skills.message}</p>}

              {skills?.length > 0 && (
                <Wrap mt={3} spacing={2}>
                  {skills.map((skill, index) => (
                    <WrapItem key={skill.uuid}>
                      <Tag size="md" borderRadius="full" variant="solid" colorScheme="blue">
                        <TagLabel>{skill.title}</TagLabel>
                        <TagCloseButton
                          onClick={(e) => {
                            e.preventDefault()
                            removeSkill(index)
                          }}
                        />
                      </Tag>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSkills([])
                        setSkillsId([])
                        setValue("skills", [], { shouldValidate: true })
                        setError("skills", { type: "manual", message: "Please select at least one skill." })
                      }}
                    >
                      Clear all
                    </Button>
                  </WrapItem>
                </Wrap>
              )}
            </FormControl>

            {/* Locations */}
            <FormControl mb={4}>
              <FormLabel>
                Location{" "}
                <Badge ml={2} colorScheme="green" variant="subtle">
                  {selectedLocations.length} selected
                </Badge>
              </FormLabel>
              <Menu
                isOpen={locationsMenu.isOpen}
                onOpen={() => {
                  locationsMenu.onOpen()
                  setLocationSearch("")
                }}
                onClose={() => {
                  locationsMenu.onClose()
                  setLocationSearch("")
                }}
                closeOnSelect
                autoSelect={false}
              >
                <MenuButton as={Button} variant="outline" width="100%" justifyContent="space-between" fontWeight="normal">
                  Add locations
                </MenuButton>
                <MenuList p={3} minW={{ base: "100%", md: "sm" }}>
                  <InputGroup size="sm">
                    <InputLeftElement pointerEvents="none">
                      <SearchIcon color="gray.400" />
                    </InputLeftElement>
                    <Input
                      placeholder="Type to search locations..."
                      value={locationSearch}
                      onChange={(e) => setLocationSearch(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") e.preventDefault()
                      }}
                      autoFocus
                    />
                  </InputGroup>
                  <div className="max-h-64 overflow-y-auto mt-2">
                    {locationsLoading ? (
                      <div className="flex items-center gap-2 p-3">
                        <Spinner size="sm" />
                        <span>Searching...</span>
                      </div>
                    ) : Array.isArray(locations) && locations.length > 0 ? (
                      locations.map((item) => {
                        const alreadySelected = selectedLocations.some((l) => l.uuid === item.uuid)
                        return (
                          <MenuItem key={item.uuid} onClick={() => handleSelectLocationItem(item)} isDisabled={alreadySelected}>
                            <div className="w-full flex items-center">
                              <span>{item.title}</span>
                              {alreadySelected && (
                                <span className="ml-auto text-xs text-blue-500 font-medium">Added</span>
                              )}
                            </div>
                          </MenuItem>
                        )
                      })
                    ) : (
                      <div className="p-3 text-gray-500">No results</div>
                    )}
                  </div>
                </MenuList>
              </Menu>

              <input type="hidden" {...register("locations")} />
              {selectedLocations?.length > 0 && (
                <Wrap mt={3} spacing={2}>
                  {selectedLocations.map((location, index) => (
                    <WrapItem key={location.uuid}>
                      <Tag size="md" borderRadius="full" variant="solid" colorScheme="green">
                        <TagLabel>{location.title}</TagLabel>
                        <TagCloseButton
                          onClick={(e) => {
                            e.preventDefault()
                            removeLocation(index)
                          }}
                        />
                      </Tag>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelectedLocations([])
                        setLocationIds([])
                        setValue("locations", [])
                      }}
                    >
                      Clear all
                    </Button>
                  </WrapItem>
                </Wrap>
              )}
            </FormControl>

            {/* Educations (Hierarchical) */}
            <FormControl mb={4}>
              <FormLabel>
                Education{" "}
                <Badge ml={2} colorScheme="purple" variant="subtle">
                  {selectedEducations.length} selected
                </Badge>
              </FormLabel>

              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3}>
                {/* Level */}
                <Select
                  placeholder="Select education level"
                  value={selectedEduLevel?.uuid ?? ""}
                  onChange={(e) => {
                    const lvl = levels.find((l) => l.uuid === e.target.value)
                    setSelectedEduLevel(lvl || null)
                    resetCourseAndBelow()
                  }}
                  bg="white"
                >
                  {levels.map((lvl) => (
                    <option key={lvl.uuid} value={lvl.uuid}>
                      {lvl.title}
                    </option>
                  ))}
                </Select>

                {/* Course */}
                <Select
                  placeholder={courses.length ? "Select course (optional)" : "No courses"}
                  value={selectedEduCourse?.uuid ?? ""}
                  onChange={(e) => {
                    const crs = courses.find((c) => c.uuid === e.target.value)
                    setSelectedEduCourse(crs || null)
                    resetSpeciality()
                  }}
                  isDisabled={!selectedEduLevel || courses.length === 0}
                  bg="white"
                >
                  {courses.map((crs) => (
                    <option key={crs.uuid} value={crs.uuid}>
                      {crs.title}
                    </option>
                  ))}
                </Select>

                {/* Speciality */}
                <Select
                  placeholder={specialities.length ? "Select speciality (optional)" : "No specialities"}
                  value={selectedEduSpeciality?.uuid ?? ""}
                  onChange={(e) => {
                    const spec = specialities.find((s) => s.uuid === e.target.value)
                    setSelectedEduSpeciality(spec || null)
                  }}
                  isDisabled={!selectedEduCourse || specialities.length === 0}
                  bg="white"
                >
                  {specialities.map((spec) => (
                    <option key={spec.uuid} value={spec.uuid}>
                      {spec.title}
                    </option>
                  ))}
                </Select>
              </SimpleGrid>

              <HStack mt={3} spacing={3}>
                <Button colorScheme="purple" onClick={addSelectedEducation} isDisabled={!selectedEduLevel}>
                  Add
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setSelectedEduLevel(null)
                    resetCourseAndBelow()
                  }}
                >
                  Clear selection
                </Button>
              </HStack>

              <input type="hidden" {...register("educations")} />

              {selectedEducations?.length > 0 && (
                <Wrap mt={3} spacing={2}>
                  {selectedEducations.map((edu, index) => (
                    <WrapItem key={edu.uuid}>
                      <Tag size="md" borderRadius="full" variant="solid" colorScheme="purple">
                        <TagLabel>{edu.title}</TagLabel>
                        <TagCloseButton
                          onClick={(e) => {
                            e.preventDefault()
                            removeEducation(index)
                          }}
                        />
                      </Tag>
                    </WrapItem>
                  ))}
                  <WrapItem>
                    <Button
                      size="xs"
                      variant="ghost"
                      onClick={() => {
                        setSelectedEducations([])
                        setEducationIds([])
                        setValue("educations", [])
                      }}
                    >
                      Clear all
                    </Button>
                  </WrapItem>
                </Wrap>
              )}
            </FormControl>

            {/* Experience */}
            <div className="mb-6">
              <label className="block text-sm font-medium mb-2">
                Experience <span className="text-red-600">*</span>
              </label>
              <HStack spacing={4} align="stretch" flexWrap="wrap">
                <Box flex="1 1 200px" minW="180px">
                  <select
                    {...register("experience_min")}
                    className="w-full border rounded-lg p-2"
                    disabled={isFresher}
                  >
                    <option value="">Minimum experience</option>
                    {[...Array(31).keys()].map((year) => (
                      <option key={year} value={year}>
                        {year} {year === 1 ? "year" : "years"}
                      </option>
                    ))}
                  </select>
                </Box>
                <Box flex="1 1 200px" minW="180px">
                  <select
                    {...register("experience_max", {
                      validate: (val) => {
                        const min = Number(watch("experience_min") || -1)
                        const max = val === "" ? -1 : Number(val)
                        if (isFresher || max === -1 || min === -1) return true
                        return max >= min || "Max experience must be >= Min experience"
                      },
                    })}
                    className="w-full border rounded-lg p-2"
                    disabled={isFresher}
                  >
                    <option value="">Maximum experience</option>
                    {[...Array(31).keys()].map((year) => (
                      <option key={year} value={year}>
                        {year} {year === 1 ? "year" : "years"}
                      </option>
                    ))}
                  </select>
                </Box>
              </HStack>
              {errors.experience_max && (
                <p className="text-red-500 text-sm mt-1">{errors.experience_max.message}</p>
              )}
              <p className="text-xs text-gray-500 mt-1">
                Tip: Leave one of the fields blank to search open-ended ranges.
              </p>
            </div>

            {/* Notice period + gender */}
            <div className="border-t pt-6 flex flex-wrap gap-3 justify-between items-center">
              <div className="flex flex-wrap gap-6">
                <div>
                  <label htmlFor="noticePeriod" className="block text-sm font-medium mb-2">
                    Notice Period
                  </label>
                  <select
                    id="noticePeriod"
                    {...register("notice_period")}
                    className="border rounded-lg py-2 pl-1"
                    disabled={isFresher}
                  >
                    <option value="">Select an option</option>
                    <option value="Immediately Available">Immediately Available</option>
                    <option value="15 Days or less">15 Days or less</option>
                    <option value="1 Months">1 Months</option>
                    <option value="2 Months">2 Months</option>
                    <option value="3 Months">3 Months</option>
                    <option value="More than 3 Months">More than 3 Months</option>
                    <option value="Serving Notice Period">Serving Notice Period</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="candidateGender" className="block text-sm font-medium mb-2">
                    Candidate Gender
                  </label>
                  <select id="candidateGender" {...register("candidate_gender")} className="border rounded-lg py-2 pl-1">
                    <option value="">Any</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                  </select>
                </div>
              </div>

              <div className="flex gap-4">
                <button
                  type="button"
                  onClick={() => {
                    reset()
                    setSkills([])
                    setSkillsId([])
                    setSelectedLocations([])
                    setLocationIds([])
                    setSelectedEducations([])
                    setEducationIds([])
                    setSkillSearch("")
                    setLocationSearch("")
                    clearErrors()
                  }}
                  className="px-4 py-2 text-blue-600 border rounded-lg hover:bg-gray-100"
                >
                  Reset
                </button>
                <Tooltip label={skills.length === 0 ? "Add at least one skill to search" : ""} isDisabled={skills.length > 0}>
                  <button
                    type="submit"
                    className={`px-4 py-2 text-white rounded-lg ${skills.length === 0 ? "bg-blue-300 cursor-not-allowed" : "bg-blue-600 hover:bg-blue-700"
                      }`}
                    disabled={skills.length === 0}
                  >
                    Search Candidate
                  </button>
                </Tooltip>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}

export default CandidateSearchForm